var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { noteSchemaInstanceMap, noteSchema, getSchemaDefaultValues, } from 'constants/note-schemas';
import { useEffect, useState } from 'react';
import { useNote, useNoteTemplate } from 'shared/hooks';
import { defaultNoteSchemasByNoteType, noteSchemaTransitions, useNoteSchemaContext, } from 'shared/features/note';
import { syncStorageKey } from 'constants/autosave';
import { useDefaultNoteMethod } from './useDefaultNoteMethod';
var setDefaultAppointmentMethod = function (ehrParse, method, location) {
    return __assign(__assign({}, ehrParse), { sessionInfo: __assign(__assign({}, ehrParse === null || ehrParse === void 0 ? void 0 : ehrParse.sessionInfo), { method: method, location: location }) });
};
/**
 * This pulls things we may not want to copy forward off of the template
 * response.  Eventually we should probably get the api to not send, but
 * today it's faster to just ignore things until requirements settle out.
 */
var removeAndModifyFieldsForCopyForward = function (template) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    if ((_a = template === null || template === void 0 ? void 0 : template.assessment) === null || _a === void 0 ? void 0 : _a.diagnoses) {
        var filteredDiagnoses = (_c = (_b = template === null || template === void 0 ? void 0 : template.assessment) === null || _b === void 0 ? void 0 : _b.diagnoses) === null || _c === void 0 ? void 0 : _c.filter(function (diagnosis) { return diagnosis.deprecated !== true; });
        if ((filteredDiagnoses === null || filteredDiagnoses === void 0 ? void 0 : filteredDiagnoses.length) !== ((_e = (_d = template === null || template === void 0 ? void 0 : template.assessment) === null || _d === void 0 ? void 0 : _d.diagnoses) === null || _e === void 0 ? void 0 : _e.length)) {
            template.assessment.diagnoses = filteredDiagnoses;
        }
    }
    // https://springhealth.atlassian.net/browse/CGX-794
    return {
        subjective: {},
        memberHistory: {
            substanceUseAbuse: {
                substances: (_g = (_f = getSchemaDefaultValues(noteSchemaInstanceMap[noteSchema.intake]).memberHistory) === null || _f === void 0 ? void 0 : _f.substanceUseAbuse) === null || _g === void 0 ? void 0 : _g.substances,
            },
        },
        objective: {
            // no screeningMeasures.
            mentalStatusExam: __assign({}, (_h = template === null || template === void 0 ? void 0 : template.objective) === null || _h === void 0 ? void 0 : _h.mentalStatusExam),
        },
        assessment: __assign({}, template === null || template === void 0 ? void 0 : template.assessment),
        plan: __assign({}, template === null || template === void 0 ? void 0 : template.plan),
        safetyPlan: __assign({}, ((_j = template === null || template === void 0 ? void 0 : template.safetyPlan) !== null && _j !== void 0 ? _j : {})),
    };
};
var copyForwardVitals = function (template, ehrParse) {
    var _a, _b, _c, _d, _e;
    if ((_a = template === null || template === void 0 ? void 0 : template.objective) === null || _a === void 0 ? void 0 : _a.vitals) {
        return __assign(__assign({}, ehrParse), { objective: __assign(__assign({}, ehrParse === null || ehrParse === void 0 ? void 0 : ehrParse.objective), { vitals: __assign({}, (_b = template === null || template === void 0 ? void 0 : template.objective) === null || _b === void 0 ? void 0 : _b.vitals) }) });
    }
    return __assign(__assign({}, ehrParse), { objective: {
            vitals: __assign({}, (_d = (_c = getSchemaDefaultValues(noteSchemaInstanceMap[noteSchema.adultMedManagementIntake])) === null || _c === void 0 ? void 0 : _c.objective) === null || _d === void 0 ? void 0 : _d.vitals),
        }, plan: __assign({}, (_e = getSchemaDefaultValues(noteSchemaInstanceMap[noteSchema.adultMedManagementIntake])) === null || _e === void 0 ? void 0 : _e.plan) });
};
var schemaCopyForwardModifiers = (_a = {},
    _a[noteSchema.intake] = removeAndModifyFieldsForCopyForward,
    _a[noteSchema.careNavigatorInitial] = function (template) {
        var _a;
        return ({
            safetyPlan: __assign({}, ((_a = template === null || template === void 0 ? void 0 : template.safetyPlan) !== null && _a !== void 0 ? _a : {})),
        });
    },
    _a[noteSchema.adultMedManagementIntake] = function (template) {
        return copyForwardVitals(template, removeAndModifyFieldsForCopyForward(template));
    },
    _a);
/**
 * We don't want to do certain things (like render checkbox lists)
 * before the data that will pre-populate the form is fully available
 * This takes care of that.
 */
export var useInitialForm = function (_a) {
    var noteId = _a.noteId;
    var templateInitialized = useInitialFormFromTemplate({
        noteId: noteId,
    });
    var _b = useInitialFormFromExistingNote({
        noteId: noteId,
    }), existingInitialized = _b.formIsInitialized, getNote = _b.getNote;
    var formIsInitialized = templateInitialized || existingInitialized;
    return { getNote: getNote, formIsInitialized: formIsInitialized };
};
export var useInitialFormFromTemplate = function (_a) {
    var noteId = _a.noteId;
    var _b = useState(false), formIsInitialized = _b[0], setFormIsInitialized = _b[1];
    var getFormForSchema = useNoteSchemaContext().getFormForSchema;
    var noteTemplate = useNoteTemplate();
    var defaultMethod = useDefaultNoteMethod();
    useEffect(function () {
        if (noteTemplate.data && !noteId && !formIsInitialized && defaultMethod) {
            // initialize the default note with passed through data
            for (var _i = 0, _a = Object.entries(schemaCopyForwardModifiers); _i < _a.length; _i++) {
                var _b = _a[_i], schema = _b[0], modifier = _b[1];
                getFormForSchema(schema).reset(setDefaultAppointmentMethod(modifier(noteTemplate.data), defaultMethod.method, defaultMethod.location));
            }
            setFormIsInitialized(true);
        }
    }, [noteTemplate.data, defaultMethod]);
    return formIsInitialized;
};
export var useInitialFormFromExistingNote = function (_a) {
    var noteId = _a.noteId;
    var _b = useState(false), formIsInitialized = _b[0], setFormIsInitialized = _b[1];
    var _c = useNoteSchemaContext(), setNoteType = _c.setNoteType, send = _c.send, getFormForSchema = _c.getFormForSchema;
    var getNote = useNote(noteId);
    useEffect(function () {
        var _a, _b, _c, _d, _e, _f, _g;
        if (getNote.data && !formIsInitialized) {
            // initialize the note with existing data
            var baseForm = getFormForSchema(null);
            baseForm.reset(__assign(__assign({}, baseForm.getValues()), { sessionInfo: getNote.data.sessionInfo }));
            var loadedSchema = ((_a = getNote.data.noteSchema) !== null && _a !== void 0 ? _a : defaultNoteSchemasByNoteType[getNote.data.sessionInfo.noteType]); // this hook is not used for non form schemas
            var form = getFormForSchema(loadedSchema);
            form.reset(noteSchemaInstanceMap[loadedSchema].prepareExistingNote(getNote.data));
            setNoteType(getNote.data.sessionInfo.noteType);
            window.localStorage.setItem(syncStorageKey((_b = getNote.data.id) !== null && _b !== void 0 ? _b : ''), (_d = (_c = getNote.data.updatedAt) !== null && _c !== void 0 ? _c : getNote.data.createdAt) !== null && _d !== void 0 ? _d : '');
            window.sessionStorage.setItem(syncStorageKey((_e = getNote.data.id) !== null && _e !== void 0 ? _e : ''), (_g = (_f = getNote.data.updatedAt) !== null && _f !== void 0 ? _f : getNote.data.createdAt) !== null && _g !== void 0 ? _g : '');
            send(noteSchemaTransitions[loadedSchema]);
            setFormIsInitialized(true);
        }
    }, [getNote.data]);
    return { formIsInitialized: formIsInitialized, getNote: getNote };
};
